import React, { useEffect, useState } from 'react';
import { Alert, Card, CardFooter, CardBody, Row, Col, Label } from 'reactstrap';
import { orderBy, find } from 'lodash';
import { Button, Field } from '@cordata/buffet';
import { Form, Formik, useFormikContext } from 'formik';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { all, spread } from 'axios';
import useApi from '~/utils/api/hook';
import PropTypes from 'prop-types';

import {
  toastSuccess,
} from '~/utils';
import Dropzone from '~/components/common/Dropzone';

const Fields = () => {
  const { setFieldValue, values } = useFormikContext();

  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [availableDivisions, setAvailableDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState([]);
  const [linkVisible, setLinkVisible] = useState(true);
  const { api } = useApi();

  useEffect(() => {
    api.get('/organization')
      .then(r => r.data.data)
      .then(r => orderBy(r, 'attributes.orgName'))
      .then((r) => {
        setAvailableOrganizations(r);

        if (values.orgUid) {
          setFieldValue('orgUid', find(r, ['id', values.orgUid]));
        }
      })
      .then(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (values.orgUid && values.orgUid.id) {
      api.get(`/division?org-uid=${values.orgUid.id}`)
        .then(r => r.data.data)
        .then(r => orderBy(r, 'attributes.divisionName'))
        .then((r) => {
          setAvailableDivisions(r);

          if (values.divisionUid) {
            setFieldValue('divisionUid', find(r, ['id', values.divisionUid]));
          }
        });
    }
  }, [values.orgUid]);

  useEffect(() => {
    if (values.file) {
      setLinkVisible(false);
      setFieldValue('imageLink', values.file.base64);
    }
  }, [values.file]);

  return (
    <React.Fragment>
      <Row form>
        <Col>
          <Field
            name="orgUid"
            label="Organization"
            type="select"
            options={availableOrganizations}
            getOptionValue={o => o.id}
            getOptionLabel={o => o.attributes.orgName}
            loading={isLoading}
            disabled={values.id}
            required
          />
        </Col>

        <Col>
          <Field
            name="divisionUid"
            label="Division"
            type="select"
            options={availableDivisions}
            getOptionValue={o => o.id}
            getOptionLabel={o => o.attributes.divisionName}
            disabled={!values.orgUid || values.id}
            required
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Label className="text-primary d-block">
            Upload file
          </Label>

          <Dropzone
            setFile={f => setFieldValue('file', f)}
            acceptFileTypes="image/*"
          />
        </Col>

        <Col sm>
          {linkVisible && (
          <Field
            name="imageLink"
            type="url"
            required
          />
                )}

          <Label className="text-primary d-block">
            Preview
          </Label>

          <a target="_blank" rel="noreferrer" href={values.imageLink}>
            <img className="mw-100" src={values.imageLink} alt="" />
          </a>
        </Col>
      </Row>
    </React.Fragment>
  );
};

const DivisionLogoForm = ({ data }) => {
  const history = useHistory();
  const { api } = useApi();

  return (
    <Formik
      validationSchema={Yup.object().shape({
    imageLink: Yup.string().required(),
    divisionUid: Yup.string().required(),
    orgUid: Yup.string().required(),
  })}

      initialValues={{
      ...data,
      ...data.attributes,
    }}

      onSubmit={(values, { setErrors, setSubmitting }) => {
    const s3uploadPromise = values.file
      ? api
        .post('/s3-upload', {
          ...values.file,
          bucket: process.env.REACT_APP_IMAGES_S3_BUCKET,
        })
      : undefined;

    all([
      s3uploadPromise,
    ])
      .then(spread((r) => {
        const p = {
          orgUid: values.orgUid.id,
          divisionUid: values.divisionUid.id,
          imageLink: r ? r.data.link : values.imageLink,
        };

        const url = `/division-logo${values.id ? `/${values.id}` : ''}`;
        const method = values.id ? 'put' : 'post';

        api[method](url, p)
          .then(() => toastSuccess())
          .then(() => history.push('/organization/division-logos'))
          .catch(e => setErrors({ server: e.errors }))
          .then(() => setSubmitting(false));
      }))
      .catch(console.error)
      .then(() => setSubmitting(false));
  }}

      displayName="DivisionLogosForm"
      enableReinitialize
    >
      {({ values, isSubmitting }) => (
        <Card>
          <Form noValidate>
            <CardBody>
              {values.id && (
                <Alert color="info">
                  org_uid and division_uid cannot be changed on tables outside of
                  the config schema at this time. In order to move this item to
                  another org or division, you must delete and re-create it with
                  the new org and division.
                </Alert>
              )}

              <Fields />
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                <Button
                  type="cancel"
                  disabled={isSubmitting}
                  tag={Link}
                  to="/organization/division-logos"
                >
                  Cancel
                </Button>

                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Working ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>
          </Form>
        </Card>
    )}
    </Formik>
  );
};

DivisionLogoForm.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default DivisionLogoForm;
